import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { AudienceType } from "@/types/work";
import {
  MODE_STORAGE_KEY,
  applyModeClass,
  normalizeMode,
  readStoredMode,
  storeMode,
} from "@/lib/theme";

interface ModeContextValue {
  mode: AudienceType;
  setMode: (mode: AudienceType) => void;
  isFemale: boolean;
}

const ModeContext = createContext<ModeContextValue | null>(null);

export function ModeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<AudienceType>(readStoredMode);

  useEffect(() => {
    applyModeClass(mode);
    storeMode(mode);
  }, [mode]);

  // 別タブで切り替えた場合も同じ配色に追従させる
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== MODE_STORAGE_KEY) return;
      setModeState(normalizeMode(event.newValue));
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const setMode = useCallback((next: AudienceType) => {
    // class を先に切り替えて、状態更新を待たずに配色を反映する
    applyModeClass(next);
    setModeState(next);
  }, []);

  const value = useMemo<ModeContextValue>(
    () => ({ mode, setMode, isFemale: mode === "female" }),
    [mode, setMode],
  );

  return <ModeContext.Provider value={value}>{children}</ModeContext.Provider>;
}

export function useMode(): ModeContextValue {
  const context = useContext(ModeContext);
  if (!context) {
    throw new Error("useMode must be used within ModeProvider");
  }
  return context;
}

export { MODE_STORAGE_KEY };